import { FC, useRef, useState } from 'react';
import CSVReader from 'features/csv-reader/csv-reader';
import Button from 'common/components/buttons/button';
import { RootState } from 'app/store';
import LoadingBar from 'common/components/loading/loading-bar';
import { useTypedSelector } from 'common/hooks/store';
import {
  onNumberOfOpportunityItemsToUploadChanged,
  onNumberOfOpportunityItemsUploadedChanged,
  resetSlice,
  uploadOpportunityPercentageSelector,
  uploadStateSelector
} from '../uploads.slice';
import { useDispatch, useSelector } from 'react-redux';
import { onError, onSuccess } from 'features/toast/toast.slice';
import { OpportunityDto } from 'features/opportunities/types/opportunityDto';
import { useUploadOpportunitiesMutation } from 'app/apis/publicCommandApi';
import { ButtonTheme } from 'common/components/buttons/button-theme';
import { parseAdditionalDataProps } from 'common/utils/additional-data-properties-helpers';

export interface OpportunityCsvRow {
  OpportunityDateTime: string;
  CurrencyCode: string;
  OpportunityValue: string;
  PipelineStage: string;
  EmailAddress: string;
  PayloadValue: string;
  RaId: string;
  PhoneNumber: string;
  [LabelKey: string]: string;
  Reference: string;
}

export interface DuplicateOpportunityCsvRow {
  count: number;
  row: OpportunityCsvRow;
}

// Move to common function
const createVisitorIdentifierOptions = (
  row: OpportunityCsvRow
): { key: string; value: string }[] => {
  const visitorIdentifierOptions = [];

  if (row.EmailAddress) {
    visitorIdentifierOptions.push({
      key: 'EmailAddress',
      value: row.EmailAddress
    });
  }

  if (row.PayloadValue) {
    visitorIdentifierOptions.push({
      key: 'PayloadValue',
      value: row.PayloadValue
    });
  }

  if (row.RaId) {
    visitorIdentifierOptions.push({ key: 'RaId', value: row.RaId });
  }

  if (row.PhoneNumber) {
    visitorIdentifierOptions.push({
      key: 'PhoneNumber',
      value: row.PhoneNumber
    });
  }

  return visitorIdentifierOptions;
};

const handleDuplicates = (
  rows: OpportunityCsvRow[]
): DuplicateOpportunityCsvRow[] => {
  const allDuplicatesInCSV = rows.reduce<OpportunityCsvRow[]>((dupes, row) => {
    const existingDuplicate = dupes.find(
      (d) =>
        d.RaId === row.RaId &&
        d.EmailAddress === row.EmailAddress &&
        d.PhoneNumber === row.PhoneNumber &&
        d.PayloadValue === row.PayloadValue &&
        d.OpportunityDateTime === row.OpportunityDateTime &&
        d.PipelineStage === row.PipelineStage
    );

    return existingDuplicate ? [...dupes, existingDuplicate] : dupes;
  }, []);

  const aggregatedDuplicates: DuplicateOpportunityCsvRow[] = [];

  for (const duplicate of allDuplicatesInCSV) {
    const aggregateDuplicate = aggregatedDuplicates.find(
      (a) =>
        a.row.RaId === duplicate.RaId &&
        a.row.EmailAddress === duplicate.EmailAddress &&
        a.row.PhoneNumber === duplicate.PhoneNumber &&
        a.row.PayloadValue === duplicate.PayloadValue &&
        a.row.OpportunityDateTime === duplicate.OpportunityDateTime &&
        a.row.PipelineStage === duplicate.PipelineStage
    );

    if (aggregateDuplicate) {
      aggregateDuplicate.count = aggregateDuplicate.count + 1;
    } else {
      aggregatedDuplicates.push({
        count: 1,
        row: duplicate
      });
    }
  }

  return aggregatedDuplicates;
};

const isOpportunityType = (input: any): input is OpportunityCsvRow => {
  const schema: Record<keyof OpportunityCsvRow, string> = {
    OpportunityDateTime: 'string',
    CurrencyCode: 'string',
    OpportunityValue: 'string',
    PipelineStage: 'string',
    EmailAddress: 'string',
    PayloadValue: 'string',
    RaId: 'string',
    PhoneNumber: 'string'
  };

  const missingProperties = Object.keys(schema).filter(
    (key) => input[key] === undefined
  );

  return missingProperties.length === 0;
};

const opportunityIsValid = (opportunity: OpportunityDto): boolean =>
  !isNaN(opportunity.opportunityValue) &&
  opportunity.opportunityDateTime.length > 0 &&
  opportunity.pipelineStage.length > 0 &&
  opportunity.currencyCode.length > 0;

export const UploadOpportunities: FC = () => {
  const dispatch = useDispatch();
  const isFirstRender = useRef(true);

  if (isFirstRender.current) {
    dispatch(resetSlice());
    isFirstRender.current = false;
  }

  const { siteId } = useSelector((state: RootState) => state.selectedSite);
  const { numberOfOpportunityItemsToUpload } =
    useTypedSelector(uploadStateSelector);

  const uploadPercentage = useTypedSelector(
    uploadOpportunityPercentageSelector
  );

  const [opportunitiesToUpload, setOpportunitiesToUpload] = useState<
    OpportunityDto[]
  >([]);
  const [fileInputName, setFileInputName] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [hasUploadErrored, setHasUploadErrored] = useState<boolean>(false);
  const [duplicateRows, setDuplicateRows] = useState<
    DuplicateOpportunityCsvRow[]
  >([]);

  const handleCSVRead = (data: OpportunityCsvRow[], fileName: string) => {
    const duplicates = handleDuplicates(data);
    const validOpportunities = data.filter(
      (opportunityCsvRow) =>
        opportunityCsvRow && isOpportunityType(opportunityCsvRow)
    );
    const opportunitiesToUpload: OpportunityDto[] = validOpportunities
      .map((opportunityCsvRow) => {
        const labelkeys = Object.keys(opportunityCsvRow).filter(
          (propertyName) => propertyName.startsWith('Label')
        );

        const additionalDataProperties = parseAdditionalDataProps(
          opportunityCsvRow.AdditionalDataProperties
        ).map((prop) => ({ name: prop.key, value: prop.value }));

        const opportunity: OpportunityDto = {
          opportunityValue:
            opportunityCsvRow.OpportunityValue.length < 1
              ? NaN
              : +opportunityCsvRow.OpportunityValue,
          opportunityDateTime: opportunityCsvRow.OpportunityDateTime,
          pipelineStage: opportunityCsvRow.PipelineStage,
          labels: labelkeys
            .map((labelKey) => opportunityCsvRow[labelKey])
            .filter((labelValue) => labelValue !== ''),
          currencyCode: opportunityCsvRow.CurrencyCode,
          visitorIdentifierOptions:
            createVisitorIdentifierOptions(opportunityCsvRow),
          additionalDataProperties,
          reference: opportunityCsvRow.Reference
        };
        return opportunity;
      })
      .filter(
        (opportunityRow): opportunityRow is OpportunityDto =>
          opportunityRow !== null && opportunityRow !== undefined
      );

    const areOpportunityRecordsValid =
      opportunitiesToUpload.every(opportunityIsValid);

    if (!areOpportunityRecordsValid) {
      dispatch(
        onError({
          title: 'Invalid File',
          description:
            'The selected file has invalid values. Please append the file and try again.'
        })
      );
      return;
    }

    setOpportunitiesToUpload(opportunitiesToUpload);
    setFileInputName(fileName);
    setHasUploadErrored(false);
    setDuplicateRows(duplicates);
    dispatch(
      onNumberOfOpportunityItemsToUploadChanged(opportunitiesToUpload.length)
    );
    dispatch(onNumberOfOpportunityItemsUploadedChanged(0));
  };

  const [uploadOpportunitiesMutation] = useUploadOpportunitiesMutation();

  const UploadOpportunities = () => {
    setIsUploading(true);

    void uploadOpportunitiesMutation({
      siteId,
      opportunities: opportunitiesToUpload
    })
      .unwrap()
      .then((result: any) => {
        if (result.isUploadSuccessful) {
          setIsUploading(false);

          dispatch(
            onSuccess({
              title: 'Import Success',
              description:
                'The import of opportunities were successfully uploaded. You can now navigate away from this page.'
            })
          );
        } else {
          setHasUploadErrored(true);
          const rowNumber = result.itemNumberFailure;
          const description = result.errors
            ? `Error uploading row ${rowNumber}. ${Object.values(
                result.errors
              ).join('. ')}`
            : 'An error occurred when uploading opportunities. If the issue persists then please contact support.';
          dispatch(
            onError({
              title: 'An Error Occurred Uploading Opportunities',
              description
            })
          );
        }
      })
      .finally(() => {
        setOpportunitiesToUpload([]);
        setFileInputName('');
        setIsUploading(false);
        setDuplicateRows([]);
        dispatch(onNumberOfOpportunityItemsToUploadChanged(0));
        dispatch(onNumberOfOpportunityItemsUploadedChanged(0));
      });
  };

  return (
    <>
      <CSVReader onCSVRead={handleCSVRead} />
      <div className="mt-3">
        <span className="font-medium">File to upload:</span>
        <span className="ml-1">{fileInputName}</span>
      </div>
      <div className="mt-3">
        <span className="font-medium">
          Number of opportunity records to upload:
        </span>
        <span className="ml-1">{numberOfOpportunityItemsToUpload}</span>
      </div>
      <div className="mt-3">
        <strong> Duplicates</strong>
        {duplicateRows.length < 1 ? (
          <>
            <div>No duplicates found</div>
          </>
        ) : (
          duplicateRows.map((duplicate, index) => {
            return (
              <div key={index} className="mt-2 flex items-center">
                <strong>{duplicate.count} instances of</strong>
                <span className="ml-2">
                  {duplicate.row.OpportunityDateTime}
                </span>
                <span className="ml-2">{duplicate.row.PipelineStage}</span>
                {duplicate.row.RaId ? (
                  <span className="ml-2">{duplicate.row.RaId}</span>
                ) : (
                  <></>
                )}
                {duplicate.row.EmailAddress ? (
                  <span className="ml-2">{duplicate.row.EmailAddress}</span>
                ) : (
                  <></>
                )}{' '}
                {duplicate.row.PhoneNumber ? (
                  <span className="ml-2">{duplicate.row.PhoneNumber}</span>
                ) : (
                  <></>
                )}{' '}
                {duplicate.row.PayloadValue ? (
                  <span className="ml-2">{duplicate.row.PayloadValue}</span>
                ) : (
                  <></>
                )}
              </div>
            );
          })
        )}
      </div>
      <div className="mt-5">
        <Button
          identifier="uploadOpportunities"
          theme={ButtonTheme.PRIMARY}
          isDisabled={opportunitiesToUpload.length < 1}
          isLoading={isUploading}
          onClick={() => UploadOpportunities()}
        >
          <p>Upload Opportunities</p>
        </Button>
      </div>

      <div className="mt-3">
        {isUploading ? (
          <LoadingBar
            percentage={uploadPercentage}
            hasError={hasUploadErrored}
          />
        ) : (
          <></>
        )}
      </div>
    </>
  );
};
